/**
 * Navigation Ref — Imperative navigation for non-React code
 *
 * Purpose: Allows socket handlers, middleware, and other plain functions
 * to trigger React Router navigation without access to useNavigate().
 *
 * How it works:
 * 1. A React component (ChatLayout) calls `setNavigateRef(navigate)` on mount
 * 2. Non-React code calls `navigateToChat(chatId)` or `navigateToChatList()` to navigate
 * 3. The ref bridges the gap between React Router and imperative code
 *
 * IMPORTANT:
 *   This exists solely to support the architectural rule defined in
 *   useChatRouteSync — all navigation MUST go through the URL so that
 *   the sync hook can update Zustand state accordingly.
 */

import type { NavigateFunction } from "react-router-dom";

let _navigate: NavigateFunction | null = null;

/**
 * Called once from ChatLayout to store the navigate function reference.
 */
export const setNavigateRef = (nav: NavigateFunction) => {
  _navigate = nav;
};

/**
 * Navigate to a chat by ID (URL-first pattern).
 *
 * @param chatId - The chat to navigate to
 * @param options.replace - Use replace navigation (default: false).
 *   Set to true for redirects (deletion, cleanup), false for user-initiated navigation.
 */
export const navigateToChat = (
  chatId: string,
  options?: { replace?: boolean },
) => {
  if (_navigate) {
    _navigate(`/${chatId}`, { replace: options?.replace });
  } else {
    // eslint-disable-next-line no-console
    console.warn("navigateToChat: navigate ref not initialized yet");
  }
};

/**
 * Navigate away from the current chat to the chat list (URL-first pattern).
 *
 * Used when a chat is deleted, user is removed, or chat creation fails.
 */
export const navigateToChatList = () => {
  if (_navigate) {
    _navigate("/", { replace: true });
  } else {
    // eslint-disable-next-line no-console
    console.warn("navigateToChatList: navigate ref not initialized yet");
  }
};

/**
 * Navigate to the login screen with `replace` — used by terminal session
 * teardowns (account deleted) so the dead app shell is not left in history.
 * Falls back to Protected's own redirect when the ref is not initialized.
 */
export const navigateToLogin = () => {
  if (_navigate) {
    _navigate("/login", { replace: true });
  } else {
    // eslint-disable-next-line no-console
    console.warn("navigateToLogin: navigate ref not initialized yet");
  }
};
